import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { InventoryResponse } from '@/types';
import type { EventCatalogLocality } from '@/hooks/useEventCatalog';
import { Badge, Button, Card, CardTitle, type BadgeTone } from '@/components/ui';
import { formatPct } from '@/lib/format';
import { useMoney } from '@/hooks/useMoney';
import { WaitlistCta } from './WaitlistCta';

export type SeatPickerSelection = {
  stageId: string;
  stageName: string;
  localityId: string;
  localityName: string;
  quantity: number;
  unitPriceCents: number | null;
};

type SeatPickerProps = {
  inventory: InventoryResponse;
  /** Locality price lookup keyed by localityId. From `useEventCatalog`. */
  localityCatalog: Map<string, EventCatalogLocality>;
  onAdd: (selection: SeatPickerSelection) => void;
  /** ISO-4217 del evento (Ola 3 multimoneda). Default COP. */
  currency?: string | null;
  /** Evento — necesario para la lista de espera de las localidades agotadas. */
  eventId: string;
};

function inventoryToneFromPct(pct: number): BadgeTone {
  if (pct >= 90) return 'danger';
  if (pct >= 60) return 'warning';
  if (pct > 0) return 'info';
  return 'success';
}

/**
 * SeatPicker — list+grid (no SVG yet) per kickoff:
 *   "Seat selection: list+grid simple primero. Si terminás antes de Día 4,
 *    agregás SVG estático con click en localidad. Konva queda post-H7."
 *
 * Renders one Card per stage with nested locality rows. Each locality shows:
 *   - Name + price
 *   - Capacity / available badge (tone scales with sold %)
 *   - Quantity stepper (1..available, bounded)
 *   - "Agregar" button (disabled when available < quantity)
 *
 * Per-locality state is local to the picker. Adding pushes into the cart
 * store via `onAdd` callback — this component knows nothing about Zustand.
 *
 * Animation: subtle pulse on the available badge when count changes (handled
 * via `key=` on the Badge so React replaces the node and `animate-pulse`
 * runs once per update — keeps it tasteful, no full-blown framer-motion).
 */
export function SeatPicker({
  inventory,
  localityCatalog,
  onAdd,
  currency,
  eventId,
}: SeatPickerProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      {inventory.stages.map((stage) => (
        <StageBlock
          key={stage.stageId}
          stage={stage}
          localityCatalog={localityCatalog}
          onAdd={onAdd}
          currency={currency}
          eventId={eventId}
        />
      ))}
    </div>
  );
}

function StageBlock({
  stage,
  localityCatalog,
  onAdd,
  currency,
  eventId,
}: {
  stage: InventoryResponse['stages'][number];
  localityCatalog: Map<string, EventCatalogLocality>;
  onAdd: SeatPickerProps['onAdd'];
  currency?: string | null;
  eventId: string;
}): JSX.Element {
  const { t } = useTranslation('events');
  const stageTone = inventoryToneFromPct(stage.soldPct);
  return (
    <Card variant="default" padded>
      <div className="flex items-start justify-between gap-3">
        <CardTitle>{stage.stageName}</CardTitle>
        <Badge tone={stageTone}>
          {t('seatPicker.stageBadge', {
            pct: formatPct(stage.soldPct, 0),
            available: stage.available,
          })}
        </Badge>
      </div>
      <div className="mt-4 divide-y divide-fc-line">
        {stage.localities.map((loc) => {
          const cat = localityCatalog.get(loc.localityId);
          return (
            <LocalityRow
              key={loc.localityId}
              stageId={stage.stageId}
              stageName={stage.stageName}
              locality={loc}
              priceCents={cat?.priceFullCents ?? null}
              onAdd={onAdd}
              currency={currency}
              eventId={eventId}
            />
          );
        })}
        {stage.localities.length === 0 && (
          <p className="py-4 text-sm text-fc-muted">{t('seatPicker.noLocalities')}</p>
        )}
      </div>
    </Card>
  );
}

function LocalityRow({
  stageId,
  stageName,
  locality,
  priceCents,
  onAdd,
  currency,
  eventId,
}: {
  stageId: string;
  stageName: string;
  locality: InventoryResponse['stages'][number]['localities'][number];
  priceCents: number | null;
  onAdd: SeatPickerProps['onAdd'];
  currency?: string | null;
  eventId: string;
}): JSX.Element {
  const { t } = useTranslation('events');
  const { moneyCents } = useMoney();
  const [quantity, setQuantity] = useState(1);

  const maxQty = Math.max(0, Math.min(locality.available, 10)); // cap UI at 10 per click
  const clamped = Math.min(Math.max(1, quantity), Math.max(1, maxQty));
  const disabled = locality.available < clamped || maxQty < 1;
  const soldOut = locality.available === 0;

  const availabilityLabel = useMemo(() => {
    if (locality.available === 0) return t('seatPicker.soldOut');
    if (locality.available < 5) return t('seatPicker.left', { count: locality.available });
    return t('seatPicker.available', { count: locality.available });
  }, [locality.available, t]);
  const availabilityTone: BadgeTone =
    locality.available === 0 ? 'danger' : locality.available < 5 ? 'warning' : 'success';

  return (
    <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-fc-ink">{locality.name}</span>
          <Badge
            // `key` changes when count changes → re-mount + animate-pulse runs once
            key={`${locality.localityId}-${locality.available}`}
            tone={availabilityTone}
            className="animate-pulse"
          >
            {availabilityLabel}
          </Badge>
        </div>
        <div className="mt-1 text-sm text-fc-muted">
          {t('seatPicker.capacity', { capacity: locality.capacity, reserved: locality.reserved })}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className="text-base font-semibold text-fc-ink">
            {moneyCents(priceCents, currency)}
          </div>
          {priceCents === null && (
            <div className="text-[10px] uppercase tracking-wide text-fc-muted">
              {t('seatPicker.perUnit')}
            </div>
          )}
        </div>
        {soldOut ? (
          /* SDD-029 — localidad agotada: la puerta es la lista de espera. */
          <WaitlistCta
            eventId={eventId}
            localityId={locality.localityId}
            localityName={locality.name}
          />
        ) : (
          <>
            <QuantityStepper
              value={clamped}
              min={1}
              max={Math.max(1, maxQty)}
              onChange={setQuantity}
              disabled={maxQty < 1}
              ariaLabel={t('seatPicker.quantityFor', { name: locality.name })}
            />
            <Button
              variant="primary"
              size="md"
              disabled={disabled}
              onClick={() =>
                onAdd({
                  stageId,
                  stageName,
                  localityId: locality.localityId,
                  localityName: locality.name,
                  quantity: clamped,
                  unitPriceCents: priceCents,
                })
              }
            >
              {t('seatPicker.add')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function QuantityStepper({
  value,
  min,
  max,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  ariaLabel: string;
}): JSX.Element {
  const { t } = useTranslation('events');
  function clamp(n: number): number {
    return Math.min(max, Math.max(min, Math.floor(n)));
  }
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex items-center rounded-card border border-fc-line ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <button
        type="button"
        aria-label={t('seatPicker.decrease')}
        className="h-9 w-9 text-fc-ink hover:bg-fc-line/40 disabled:cursor-not-allowed"
        disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - 1))}
      >
        −
      </button>
      <span aria-live="polite" className="min-w-[2ch] px-2 text-center text-sm tabular-nums">
        {value}
      </span>
      <button
        type="button"
        aria-label={t('seatPicker.increase')}
        className="h-9 w-9 text-fc-ink hover:bg-fc-line/40 disabled:cursor-not-allowed"
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + 1))}
      >
        +
      </button>
    </div>
  );
}
